import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon, Crop, RotateCw, Square, Maximize } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AspectRatio {
  name: string;
  ratio: number | null; // null for free aspect
  width?: number;
  height?: number;
}

const aspectRatios: AspectRatio[] = [
  { name: 'Free', ratio: null },
  { name: 'Square (1:1)', ratio: 1 },
  { name: 'Portrait (3:4)', ratio: 3/4 },
  { name: 'Landscape (4:3)', ratio: 4/3 },
  { name: 'Widescreen (16:9)', ratio: 16/9 },
  { name: 'Instagram Post (1:1)', ratio: 1, width: 1080, height: 1080 },
  { name: 'Instagram Story (9:16)', ratio: 9/16, width: 1080, height: 1920 },
  { name: 'Facebook Cover (16:6)', ratio: 16/6, width: 1200, height: 450 },
  { name: 'Twitter Header (3:1)', ratio: 3/1, width: 1500, height: 500 },
];

export const ImageCropTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 100, height: 100 });
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>(aspectRatios[0]);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('tools.imageCrop.invalidImageError'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImageSize({ width: img.width, height: img.height });
          // Set initial crop area to center 50% of image
          const centerX = img.width * 0.25;
          const centerY = img.height * 0.25;
          const width = img.width * 0.5;
          const height = img.height * 0.5;
          setCropArea({ x: centerX, y: centerY, width, height });
        };
        img.src = e.target?.result as string;
        setOriginalImage(e.target?.result as string);
        setCroppedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAspectRatioChange = (aspectRatio: AspectRatio) => {
    setSelectedAspectRatio(aspectRatio);
    
    if (aspectRatio.ratio !== null) {
      // Adjust crop area to match aspect ratio
      const { width: imgWidth, height: imgHeight } = imageSize;
      const maxSize = Math.min(imgWidth, imgHeight) * 0.8;
      
      let newWidth, newHeight;
      if (aspectRatio.ratio > 1) {
        // Landscape
        newWidth = maxSize;
        newHeight = maxSize / aspectRatio.ratio;
      } else {
        // Portrait or square
        newHeight = maxSize;
        newWidth = maxSize * aspectRatio.ratio;
      }
      
      const newX = (imgWidth - newWidth) / 2;
      const newY = (imgHeight - newHeight) / 2;
      
      setCropArea({ x: newX, y: newY, width: newWidth, height: newHeight });
    }
  };

  const cropImage = useCallback(() => {
    if (!originalImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas size to crop area size
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      // Draw cropped portion
      ctx.drawImage(
        img,
        cropArea.x, cropArea.y, cropArea.width, cropArea.height,
        0, 0, cropArea.width, cropArea.height
      );

      const croppedDataUrl = canvas.toDataURL('image/png');
      setCroppedImage(croppedDataUrl);
    };
    img.src = originalImage;
  }, [originalImage, cropArea]);

  const downloadCropped = () => {
    if (!croppedImage) return;
    
    const link = document.createElement('a');
    link.download = 'cropped-image.png';
    link.href = croppedImage;
    link.click();
  };

  const resetCrop = () => {
    if (imageSize.width && imageSize.height) {
      const centerX = imageSize.width * 0.25;
      const centerY = imageSize.height * 0.25;
      const width = imageSize.width * 0.5;
      const height = imageSize.height * 0.5;
      setCropArea({ x: centerX, y: centerY, width, height });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    setCropArea(prev => ({
      ...prev,
      x: Math.max(0, Math.min(prev.x + deltaX, imageSize.width - prev.width)),
      y: Math.max(0, Math.min(prev.y + deltaY, imageSize.height - prev.height))
    }));
    
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Crop className="w-5 h-5" />
            <span>{t('common.imageCropTool')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.dragDropImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectImage')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Crop Controls */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.imageCrop.cropSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {aspectRatios.map((ratio) => (
                    <Button
                      key={ratio.name}
                      onClick={() => handleAspectRatioChange(ratio)}
                      variant={selectedAspectRatio.name === ratio.name ? 'primary' : 'outline'}
                      size="sm"
                      className="text-xs"
                    >
                      {ratio.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    X Position
                  </label>
                  <Input
                    type="number"
                    value={Math.round(cropArea.x)}
                    onChange={(e) => setCropArea(prev => ({ 
                      ...prev, 
                      x: Math.max(0, Math.min(parseInt(e.target.value) || 0, imageSize.width - prev.width))
                    }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Y Position
                  </label>
                  <Input
                    type="number"
                    value={Math.round(cropArea.y)}
                    onChange={(e) => setCropArea(prev => ({ 
                      ...prev, 
                      y: Math.max(0, Math.min(parseInt(e.target.value) || 0, imageSize.height - prev.height))
                    }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Width
                  </label>
                  <Input
                    type="number"
                    value={Math.round(cropArea.width)}
                    onChange={(e) => {
                      const newWidth = Math.max(10, Math.min(parseInt(e.target.value) || 10, imageSize.width - cropArea.x));
                      setCropArea(prev => ({ 
                        ...prev, 
                        width: newWidth,
                        height: selectedAspectRatio.ratio ? newWidth / selectedAspectRatio.ratio : prev.height
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Height
                  </label>
                  <Input
                    type="number"
                    value={Math.round(cropArea.height)}
                    onChange={(e) => {
                      const newHeight = Math.max(10, Math.min(parseInt(e.target.value) || 10, imageSize.height - cropArea.y));
                      setCropArea(prev => ({ 
                        ...prev, 
                        height: newHeight,
                        width: selectedAspectRatio.ratio ? newHeight * selectedAspectRatio.ratio : prev.width
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={cropImage} variant="primary">
                  <Crop className="w-4 h-4 mr-2" />
                  {t('tools.imageCrop.cropImage')}
                </Button>
                <Button onClick={resetCrop} variant="outline">
                  {t('tools.imageCrop.resetCrop')}
                </Button>
                {croppedImage && (
                  <Button onClick={downloadCropped} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    {t('tools.imageCrop.downloadCropped')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Preview */}
      {originalImage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Original Image</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative inline-block">
                <img
                  ref={imageRef}
                  src={originalImage}
                  alt="Original"
                  className="max-w-full h-auto border rounded"
                  style={{ maxHeight: '400px' }}
                />
                {/* Crop overlay */}
                <div
                  className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move"
                  style={{
                    left: `${(cropArea.x / imageSize.width) * 100}%`,
                    top: `${(cropArea.y / imageSize.height) * 100}%`,
                    width: `${(cropArea.width / imageSize.width) * 100}%`,
                    height: `${(cropArea.height / imageSize.height) * 100}%`,
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <div className="absolute inset-0 border border-white border-dashed"></div>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Original size: {imageSize.width} × {imageSize.height}px
              </p>
            </CardContent>
          </Card>

          {croppedImage && (
            <Card>
              <CardHeader>
                <CardTitle>Cropped Result</CardTitle>
              </CardHeader>
              <CardContent>
                <img
                  src={croppedImage}
                  alt="Cropped"
                  className="max-w-full h-auto border rounded"
                  style={{ maxHeight: '400px' }}
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Cropped size: {Math.round(cropArea.width)} × {Math.round(cropArea.height)}px
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Crop Information */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>Crop Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                <div className="font-medium text-blue-600 dark:text-blue-400">
                  {Math.round(cropArea.x)}, {Math.round(cropArea.y)}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Position (X, Y)</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <div className="font-medium text-green-600 dark:text-green-400">
                  {Math.round(cropArea.width)} × {Math.round(cropArea.height)}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Crop Size</div>
              </div>
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                <div className="font-medium text-purple-600 dark:text-purple-400">
                  {selectedAspectRatio.ratio ? `${selectedAspectRatio.ratio.toFixed(2)}:1` : 'Free'}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Aspect Ratio</div>
              </div>
              <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded">
                <div className="font-medium text-orange-600 dark:text-orange-400">
                  {((cropArea.width * cropArea.height) / (imageSize.width * imageSize.height) * 100).toFixed(1)}%
                </div>
                <div className="text-gray-600 dark:text-gray-400">Area Kept</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
